import React, { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../lib/api';
import AuthField from './AuthField';
import { AuthError, AuthSubmitButton } from './AuthShell';

/**
 * In-page sign in / create account modal.
 * Keeps the user on the current page (e.g. checkout) after auth.
 */
const AuthModal = ({
  open,
  onClose,
  onSuccess,
  initialMode = 'login',
  prefillEmail = '',
  contextHint = 'Stay here - tickets stay in your cart.'
}) => {
  const { login } = useAuth();
  const titleId = useId();
  const panelRef = useRef(null);
  const [mode, setMode] = useState(initialMode === 'register' ? 'register' : 'login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (!open) return undefined;

    setMode(initialMode === 'register' ? 'register' : 'login');
    setError('');
    setLoading(false);

    const email = String(prefillEmail || '').trim();
    setLoginForm((prev) => ({ ...prev, email: email || prev.email, password: '' }));
    setRegisterForm((prev) => ({
      ...prev,
      email: email || prev.email,
      password: '',
      confirmPassword: ''
    }));

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);

    // Focus first field shortly after open
    const t = setTimeout(() => {
      const input = panelRef.current?.querySelector('input');
      input?.focus();
    }, 50);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
      clearTimeout(t);
    };
  }, [open, initialMode, prefillEmail, onClose]);

  if (!open) return null;

  const switchMode = (next) => {
    setMode(next);
    setError('');
  };

  const finishAuth = (user, token) => {
    login(user, token);
    onSuccess?.(user);
    onClose?.();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await authAPI.login({
        email: loginForm.email.trim().toLowerCase(),
        password: loginForm.password
      });
      if (!data?.success) {
        setError(data?.message || 'Sign in failed');
        return;
      }
      finishAuth(data.data.user, data.data.token);
    } catch (err) {
      setError(err.message || 'Could not sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (registerForm.password !== registerForm.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    if (registerForm.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        name: registerForm.name.trim(),
        email: registerForm.email.trim().toLowerCase(),
        password: registerForm.password
      };
      const phone = registerForm.phone.trim();
      if (phone) payload.phone = phone;

      const data = await authAPI.register(payload);
      if (!data?.success) {
        setError(data?.message || 'Could not create account');
        return;
      }
      // Register returns a token - sign in immediately and stay on page
      finishAuth(data.data.user, data.data.token);
    } catch (err) {
      setError(err.message || 'Could not create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-stone-900/45 backdrop-blur-[2px]"
        onClick={onClose}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full sm:max-w-md max-h-[92vh] overflow-y-auto
          bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl shadow-stone-900/20
          border border-stone-200/80
          px-5 pt-4 pb-6 sm:p-7
          animate-[slideUp_0.22s_ease-out]"
      >
        <style>{`
          @keyframes slideUp {
            from { transform: translateY(16px); opacity: 0.85; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}</style>

        {/* Mobile drag hint */}
        <div className="sm:hidden flex justify-center mb-3">
          <span className="w-10 h-1 rounded-full bg-stone-200" />
        </div>

        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 id={titleId} className="text-xl font-bold text-gray-900 tracking-tight">
              {mode === 'login' ? 'Welcome back' : 'Create account'}
            </h2>
            <p className="text-sm text-stone-500 mt-1">{contextHint}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 w-9 h-9 rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100 flex items-center justify-center transition-colors"
            aria-label="Close dialog"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-stone-100 mb-5">
          <button
            type="button"
            onClick={() => switchMode('login')}
            className={`py-2.5 rounded-lg text-sm font-semibold transition-colors ${
              mode === 'login'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            Log in
          </button>
          <button
            type="button"
            onClick={() => switchMode('register')}
            className={`py-2.5 rounded-lg text-sm font-semibold transition-colors ${
              mode === 'register'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-stone-500 hover:text-stone-700'
            }`}
          >
            Create account
          </button>
        </div>

        <AuthError message={error} />

        {mode === 'login' ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <AuthField
              id="auth-modal-login-email"
              label="Email"
              type="email"
              name="email"
              value={loginForm.email}
              onChange={(e) => setLoginForm((p) => ({ ...p, email: e.target.value }))}
              placeholder="you@email.com"
              autoComplete="email"
              icon="envelope"
              required
            />
            <AuthField
              id="auth-modal-login-password"
              label="Password"
              type="password"
              name="password"
              value={loginForm.password}
              onChange={(e) => setLoginForm((p) => ({ ...p, password: e.target.value }))}
              placeholder="Your password"
              autoComplete="current-password"
              icon="lock"
              required
            />
            <AuthSubmitButton loading={loading} loadingText="Signing in…">
              Log in & continue
            </AuthSubmitButton>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="space-y-4">
            <AuthField
              id="auth-modal-reg-name"
              label="Full name"
              type="text"
              name="name"
              value={registerForm.name}
              onChange={(e) => setRegisterForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Your name"
              autoComplete="name"
              icon="user"
              required
            />
            <AuthField
              id="auth-modal-reg-email"
              label="Email"
              type="email"
              name="email"
              value={registerForm.email}
              onChange={(e) => setRegisterForm((p) => ({ ...p, email: e.target.value }))}
              placeholder="you@email.com"
              autoComplete="email"
              icon="envelope"
              required
            />
            <AuthField
              id="auth-modal-reg-phone"
              label="Phone (optional)"
              type="tel"
              name="phone"
              value={registerForm.phone}
              onChange={(e) => setRegisterForm((p) => ({ ...p, phone: e.target.value }))}
              placeholder="07XX XXX XXX"
              autoComplete="tel"
              icon="phone"
            />
            <AuthField
              id="auth-modal-reg-password"
              label="Password"
              type="password"
              name="password"
              value={registerForm.password}
              onChange={(e) => setRegisterForm((p) => ({ ...p, password: e.target.value }))}
              placeholder="At least 6 characters"
              autoComplete="new-password"
              icon="lock"
              required
            />
            <AuthField
              id="auth-modal-reg-confirm"
              label="Confirm password"
              type="password"
              name="confirmPassword"
              value={registerForm.confirmPassword}
              onChange={(e) => setRegisterForm((p) => ({ ...p, confirmPassword: e.target.value }))}
              placeholder="Repeat password"
              autoComplete="new-password"
              icon="lock"
              required
            />
            <p className="text-xs text-stone-400 leading-relaxed">
              By creating an account you agree to our{' '}
              <Link to="/terms-of-service" className="text-stone-700 underline underline-offset-2" target="_blank" rel="noreferrer">
                Terms
              </Link>{' '}
              and{' '}
              <Link to="/privacy-policy" className="text-stone-700 underline underline-offset-2" target="_blank" rel="noreferrer">
                Privacy Policy
              </Link>
              .
            </p>
            <AuthSubmitButton loading={loading} loadingText="Creating account…">
              Create account & continue
            </AuthSubmitButton>
          </form>
        )}

        <p className="text-center text-xs text-stone-400 mt-5">
          Guest checkout still works - this just saves tickets to your dashboard.
        </p>
      </div>
    </div>
  );
};

export default AuthModal;
